import { type MouseEvent, useEffect, useRef, useMemo, memo } from 'react'

import { HcButton } from '@hotmart-org-ca/cosmos-web/dist/react/hc-button'

import { CustomColorsUtils } from '@space-core/utils/colors'

import { SessionMode } from '@space-shell/common/components/baseStructure/enum'
import { useParentInfoStore } from '@space-shell/remotes/preview/store/parentInfo'

import { Avatar, burgerStyles, HcHeader } from './styles'

import Logo from '../logo'
import type { StructureHeaderProps } from '../types'

const HeaderProductPage = ({
  session,
  handleClickInLogo,
  membershipName,
  theme,
  customLogoUrl,
  logoAltText,
  isLoading,
  children,
  className,
  isClassRoom = false
}: StructureHeaderProps) => {
  const headerRef = useRef<HTMLHcStructureHeaderElement>(null)

  const {
    logos,
    theme: previewTheme,
    userName,
    membershipName: membershipPreviewName,
    color,
    avatarUrl
  } = useParentInfoStore()

  const logoUrl = useMemo(
    () => logos?.find((logo) => logo.type === previewTheme)?.imageUrl,
    [logos, previewTheme]
  )

  const isSessionPreviewEnable = session === SessionMode.PREVIEW
  const themeHeader = isSessionPreviewEnable ? previewTheme : theme
  const isDarkModeEnable = themeHeader === 'dark'

  const handleClickLogo = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    handleClickInLogo?.()
  }

  useEffect(() => {
    if (!headerRef.current) {
      return
    }

    const styles = document.createElement('style')
    const shadowRoot = headerRef.current.shadowRoot

    styles.id = 'custom-burger-styles'
    styles.innerHTML = burgerStyles.toString()
    shadowRoot?.prepend(styles)

    return () => {
      styles.remove()
    }
  }, [])

  return (
    <HcHeader
      id='global-header'
      slot='header'
      $session={session}
      $theme={!isSessionPreviewEnable ? theme : previewTheme}
      $isClassRoom={isClassRoom}
      class={className}
      ref={headerRef}
      context={isDarkModeEnable ? 'black' : 'default'}
      data-test='new-structure-header'>
      <div slot='brand'>
        <Logo
          isLoading={isLoading}
          defaultLogoUrl={customLogoUrl}
          membershipName={membershipName}
          logoAltText={logoAltText}
          isSessionPreviewEnable={isSessionPreviewEnable}
          previewLogoUrl={logoUrl}
          membershipPreviewName={membershipPreviewName}
          handleClickLogo={handleClickLogo}
        />
      </div>

      {children}
      <HcButton />

      {isSessionPreviewEnable && (
        <HcButton slot='actions-end' variation='tertiary' context='neutral' size='large'>
          <Avatar
            displayName={userName}
            src={avatarUrl}
            size='xsmall'
            color={color}
            $isFixedColor={CustomColorsUtils.isFixedColor(color)}
          />
        </HcButton>
      )}
    </HcHeader>
  )
}

export default memo(HeaderProductPage)
